import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { last } from 'lodash-es';
import { Observable, map, switchMap } from 'rxjs';

import {
  ActivateDeactivateCommands,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  HeatingConfigurationRegulationMode,
  HeatingConfigurationRegulationProperties,
  IsStringValueProperty,
  OperatingProgramCommandsNormal,
  OperatingProgramProperties,
  ProgramCommands,
  ProgramProperties,
  RoomChannelsEntityProperties,
  VcsOptimizationState,
} from '@vi-iot/client';

import { GTMService } from '@services';
import { InstallationFeaturesStore } from '@store';

import { HEATING_PROGRAMS, HeatingPanelService } from './heating-panel/heating-panel.service';

@Component({
  selector: 'app-temperature-schedule-manager',
  templateUrl: './temperature-schedule-manager.component.html',
  styleUrls: ['./temperature-schedule-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemperatureScheduleManagerComponent implements OnInit {
  readonly HeatingConfigurationRegulationMode = HeatingConfigurationRegulationMode;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly vcsOptimizationState = VcsOptimizationState;

  shouldDispalyHeatingPanel$: Observable<boolean>;
  heatingRegulation$: Observable<Feature<HeatingConfigurationRegulationProperties, never>>;
  eco$: Observable<Feature<OperatingProgramProperties, ActivateDeactivateCommands>>;
  comfort$: Observable<Feature<ProgramProperties, ProgramCommands>>;
  activeOperatingProgram$: Observable<
    Feature<OperatingProgramProperties, OperatingProgramCommandsNormal>
  >;
  isVCSOptimizationActive$: Observable<boolean>;

  @Input()
  circuitId: string;

  constructor(
    public heatingPanelService: HeatingPanelService,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.shouldDispalyHeatingPanel$ = this.installationFeaturesStore.shouldDispalyHeatingPanel(
      this.circuitId
    );
    this.heatingRegulation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationRegulation
    );

    this.activeOperatingProgram$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsStringValueProperty, never>(
        FeatureNames.circuitActiveProgram(this.circuitId)
      )
      .pipe(
        switchMap((feature: Feature<IsStringValueProperty, never>) =>
          this.installationFeaturesStore.selectInstallationDeviceFeature<
            OperatingProgramProperties,
            OperatingProgramCommandsNormal
          >(FeatureNames.circuitOperatingProgram(this.circuitId, feature?.properties?.value?.value))
        )
      );

    this.eco$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitOperatingProgram(this.circuitId, HEATING_PROGRAMS.ECO)
    );
    this.comfort$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitOperatingProgram(this.circuitId, HEATING_PROGRAMS.COMFORT)
    );

    this.isVCSOptimizationActive$ = this.installationFeaturesStore
      .selectRoomChannelByHeatingCircuitId(this.circuitId)
      .pipe(
        map(
          (value: Feature<RoomChannelsEntityProperties, never>) =>
            value.properties.roomInfluenceEnabled.value && value.properties.active.value
        )
      );
  }

  onProgramTemperatureChange(value: number, feature: string): void {
    const program = last(feature.split('.'));
    this.installationFeaturesStore.updateFeature(
      FeatureNames.circuitOperatingProgram(this.circuitId, program),
      value,
      'temperature',
      'targetTemperature',
      'setTemperature'
    );

    this.gtmService.track('onSet', `Room's ${program} program temperature`, 'srcHeating');
  }
}
